import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, ArrowLeft, Check, Minus, Plus, Smartphone, Monitor, Zap, Star, AlertTriangle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { useLang } from '../i18n/LanguageContext';
import { localizeKitName } from '../lib/productName';
import { localizeDescription } from '../data/productI18n';
import ShareButtons from '../components/ShareButtons';
import { ControllerSheetModal, MarkingModal, BraceletSheetModal, ControllerPickerModal } from '../components/ControllerSheetModal';
import { CONTROLLER_IMAGES, SPEC_LABELS } from '../data/controllerSpecs';
import ColorSimulator from '../components/ColorSimulator';
import DeliveryNotice from '../components/DeliveryNotice';
import { productSeoFor } from '../data/seoMeta';
import { usePricing } from '../lib/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Product images are stored as site-relative paths; crawlers need absolute URLs
const absoluteImage = (src) => (src && src.startsWith('/') ? `https://www.party-led.com${src}` : src);

const translateFeature = (feature, t) => {
  const braceletMatch = feature.match(/^(\d+)\s+bracelets? LED ultra-premium$/i);
  if (braceletMatch) return t.product.braceletFeature.replace('{n}', braceletMatch[1]);
  if (feature.includes('contrôleur au choix')) return t.product.controllerFeature;
  if (feature.includes('marquage')) return t.product.brandingFeature;
  if (feature.includes('NFC')) return t.product.nfcFeature;
  if (feature.includes('Animateur ou DJ')) return t.product.optionFeature;
  return feature;
};

const MARK_COPY = {
  fr: {
    title: 'Personnalisation du marquage', none: 'Bracelet vierge', noneDesc: 'Sans marquage ni logo',
    custom: 'Avec marquage', customBadge: 'offert', customDesc: 'Votre logo + puce NFC — offerts',
    upload: 'Importer votre logo', uploading: 'Envoi…', change: 'Changer de logo', remove: 'Retirer',
    textLabel: 'Texte à graver (optionnel)', textPh: 'Ex : Soirée Dupont · 2026',
    previewLabel: 'Aperçu indicatif', previewNote: "Rendu indicatif — une maquette précise vous est envoyée pour validation avant production.",
    delay: 'Délai en cas de rupture : 4 à 5 semaines avec marquage + lien NFC, 3 à 4 semaines sans.',
    formats: 'PNG, JPG, SVG ou WebP · 10 Mo max', added: 'Ajouté avec votre marquage',
  },
  en: {
    title: 'Marking personalisation', none: 'Blank wristband', noneDesc: 'No marking, no logo',
    custom: 'With marking', customBadge: 'free', customDesc: 'Your logo + NFC chip — included free',
    upload: 'Upload your logo', uploading: 'Uploading…', change: 'Change logo', remove: 'Remove',
    textLabel: 'Text to print (optional)', textPh: 'E.g. Summer Gala · 2026',
    previewLabel: 'Indicative preview', previewNote: 'Indicative render — a precise mock-up is sent for approval before production.',
    delay: 'Lead time if out of stock: 4–5 weeks with marking + NFC link, 3–4 weeks without.',
    formats: 'PNG, JPG, SVG or WebP · 10 MB max', added: 'Added with your marking',
  },
  ar: {
    title: 'تخصيص الطباعة', none: 'سوار سادة', noneDesc: 'بدون طباعة أو شعار',
    custom: 'مع الطباعة', customBadge: 'مجاناً', customDesc: 'شعارك + شريحة NFC — مجاناً',
    upload: 'ارفع شعارك', uploading: 'جارٍ الرفع…', change: 'تغيير الشعار', remove: 'إزالة',
    textLabel: 'نص للطباعة (اختياري)', textPh: 'مثال: حفل 2026',
    previewLabel: 'معاينة إرشادية', previewNote: 'معاينة إرشادية — يُرسَل تصميم دقيق للموافقة قبل الإنتاج.',
    delay: 'مدة التنفيذ عند نفاد المخزون: 4–5 أسابيع مع الطباعة ورابط NFC، و3–4 أسابيع بدونها.',
    formats: 'PNG أو JPG أو SVG أو WebP · 10 ميغابايت كحد أقصى', added: 'تمت الإضافة مع الطباعة',
  },
  it: {
    title: 'Personalizzazione della stampa', none: 'Bracciale neutro', noneDesc: 'Senza stampa né logo',
    custom: 'Con stampa', customBadge: 'in omaggio', customDesc: 'Il tuo logo + chip NFC — inclusi gratis',
    upload: 'Carica il tuo logo', uploading: 'Caricamento…', change: 'Cambia logo', remove: 'Rimuovi',
    textLabel: 'Testo da stampare (opzionale)', textPh: 'Es. Festa Rossi · 2026',
    previewLabel: 'Anteprima indicativa', previewNote: 'Render indicativo — un mock-up preciso viene inviato per approvazione prima della produzione.',
    delay: 'Tempi in caso di esaurimento: 4–5 settimane con stampa + link NFC, 3–4 settimane senza.',
    formats: 'PNG, JPG, SVG o WebP · max 10 MB', added: 'Aggiunto con la tua stampa',
  },
};


const StarRating = ({ rating, size = 16, interactive = false, onRate }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type={interactive ? 'button' : undefined}
        onClick={() => interactive && onRate?.(star)}
        className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
        disabled={!interactive}
      >
        <Star
          size={size}
          className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}
        />
      </button>
    ))}
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const pricing = usePricing(lang);
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: '', email: '', rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [activeController, setActiveController] = useState(null);
  const [showMarking, setShowMarking] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showBracelet, setShowBracelet] = useState(false);
  const specLabels = SPEC_LABELS[lang] || SPEC_LABELS.fr;
  const [marking, setMarking] = useState('none');
  const [logoUrl, setLogoUrl] = useState('');
  const [customText, setCustomText] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const markCopy = MARK_COPY[lang] || MARK_COPY.fr;
  const isRTL = lang === 'ar';

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/public/upload-logo`, fd);
      setLogoUrl(res.data.url);
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Upload impossible', variant: 'destructive' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const loadReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/reviews/${slug}`);
      setReviews(res.data);
    } catch (e) { /* silent */ }
  }, [slug]);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/products/${slug}`);
        setProduct(response.data);
      } catch (error) {
        toast({ title: "Erreur", description: "Produit introuvable", variant: "destructive" });
        navigate('/catalogue');
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
    loadReviews();
  }, [slug, navigate, loadReviews]);

  const addToCart = () => {
    if (!product) return;
    const perso = marking === 'custom'
      ? { _marking: 'custom', _logoUrl: logoUrl, _customText: customText }
      : { _marking: 'none', _logoUrl: '', _customText: '' };
    const item = { ...product, ...perso };
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    for (let i = 0; i < quantity; i++) existingCart.push(item);
    localStorage.setItem('cart', JSON.stringify(existingCart));
    window.dispatchEvent(new Event('cartUpdated'));
    const suffix = marking === 'custom' ? ` · ${markCopy.added}` : '';
    toast({ title: t.product.added, description: `${quantity} x ${product.name}${suffix}` });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/reviews`, {
        productSlug: slug,
        ...reviewForm,
      });
      toast({ title: t.product.reviewThanks });
      setShowReviewForm(false);
      setReviewForm({ name: '', email: '', rating: 5, comment: '' });
      loadReviews();
    } catch (err) {
      toast({ title: "Erreur", variant: "destructive" });
    } finally {
      setSubmittingReview(false);
    }
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (loading || !product) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <p className="text-white text-xl">{t.product.loading}</p>
      </div>
    );
  }

  const isLowStock = product.stock > 0 && product.stock < 10;

  const controllers = [
    { key: 'mini', icon: Zap, img: CONTROLLER_IMAGES.mini, title: t.product.controllerMini, desc: t.product.controllerMiniDesc },
    { key: 'mobile', icon: Smartphone, img: CONTROLLER_IMAGES.mobile, title: t.product.controllerMobile, desc: t.product.controllerMobileDesc },
    { key: 'pro', icon: Monitor, img: CONTROLLER_IMAGES.pro, title: t.product.controllerPro, desc: t.product.controllerProDesc },
  ];

  const seo = productSeoFor(product.slug, lang);
  const metaTitle = seo?.title || `${product.name} | Party LED - Bracelets LED pour Événements`;
  const metaDesc = seo?.description || `${product.name} - ${product.description?.substring(0, 155)}. Bracelets LED synchronisés. Livraison France & Dubai.`;
  const ogImage = absoluteImage((product.images[0] || '').replace('kit-original.webp', 'kit-original-og.jpg'));

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={`https://www.party-led.com/produit/${product.slug}`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Party LED" />
        <meta property="og:url" content={`https://www.party-led.com/produit/${product.slug}`} />
        <meta property="og:type" content="product" />
        <meta property="product:price:amount" content={pricing.htOf(product)} />
        <meta property="product:price:currency" content={pricing.currency} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "description": product.description,
          "image": product.images.map(absoluteImage),
          "brand": { "@type": "Brand", "name": "Party LED" },
          "sku": product.slug,
          "mpn": product.slug,
          "offers": {
            "@type": "Offer",
            "price": pricing.htOf(product),
            "priceCurrency": pricing.currency,
            "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "url": `https://www.party-led.com/produit/${product.slug}`,
            "seller": { "@type": "Organization", "name": "Party LED" },
            "priceValidUntil": `${new Date().getFullYear() + 1}-12-31`,
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": { "@type": "MonetaryAmount", "value": 0, "currency": "EUR" },
              "shippingDestination": [
                { "@type": "DefinedRegion", "addressCountry": "FR" },
                { "@type": "DefinedRegion", "addressCountry": "AE" }
              ],
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 2, "unitCode": "DAY" },
                "transitTime": { "@type": "QuantitativeValue", "minValue": 14, "maxValue": 21, "unitCode": "DAY" }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "applicableCountry": "FR",
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 14,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/ReturnFeesCustomerResponsibility"
            }
          },
          ...(avgRating ? {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": avgRating,
              "reviewCount": reviews.length
            }
          } : {})
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Party LED", "item": "https://www.party-led.com" },
            { "@type": "ListItem", "position": 2, "name": "Catalogue", "item": "https://www.party-led.com/catalogue" },
            { "@type": "ListItem", "position": 3, "name": product.name }
          ]
        })}</script>
      </>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-4 text-sm text-gray-500" data-testid="breadcrumb">
          <a href="/" className="hover:text-purple-400">Party LED</a>
          <span className="mx-2">/</span>
          <a href="/catalogue" className="hover:text-purple-400">{t.catalogue.title}</a>
          <span className="mx-2">/</span>
          <span className="text-gray-300">{localizeKitName(product.name, lang)}</span>
        </nav>

        {/* Back Button */}
        <Button onClick={() => navigate(-1)} variant="ghost" className="text-gray-400 hover:text-white mb-6" data-testid="product-back-btn">
          <ArrowLeft className="w-5 h-5 mr-2" />{t.product.back}
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="mb-4 aspect-square rounded-lg overflow-hidden bg-gray-900 relative">
              <img src={product.images[selectedImage]} alt={localizeKitName(product.name, lang)} className="w-full h-full object-cover" data-testid="product-main-image" loading="eager" fetchPriority="high" decoding="async" width="1100" height="733" />
              {isLowStock && (
                <div className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 animate-pulse" data-testid="low-stock-badge">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {t.product.lowStock}
                </div>
              )}
            </div>
            {product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {product.images.map((image, index) => (
                  <button key={`${product.id}-img-${index}`} onClick={() => setSelectedImage(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${selectedImage === index ? 'border-purple-600' : 'border-gray-700 hover:border-gray-600'}`}>
                    <img loading="lazy" src={image} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="text-white">
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Georgia, serif' }} data-testid="product-name">{localizeKitName(product.name, lang)}</h1>

            {/* Rating summary */}
            {avgRating && (
              <div className="flex items-center gap-3 mb-4" data-testid="avg-rating">
                <StarRating rating={Math.round(parseFloat(avgRating))} size={18} />
                <span className="text-gray-400 text-sm">{avgRating} / 5 ({reviews.length} {t.product.reviews.toLowerCase()})</span>
              </div>
            )}

            <div className="mb-6">
              <span className="text-4xl font-bold text-white" data-testid="product-price">{pricing.fmt(pricing.htOf(product))} HT<span className="block text-gray-200 text-lg font-medium mt-1">{pricing.fmt(pricing.ttcOf(product))} TTC <span className="text-gray-400 text-sm font-normal">· TVA {pricing.vatRate} % incluse</span></span></span>
            </div>

            <p className="text-gray-300 text-lg mb-6 leading-relaxed">{localizeDescription(product.slug, product.description, lang)}</p>

            {/* Features */}
            <div className="mb-8" data-testid="product-features">
              <h3 className="text-xl font-semibold mb-4">{t.product.features} :</h3>
              <ul className="space-y-2">
                {product.features.map((feature, idx) => {
                  const isMarking = feature.includes('marquage');
                  const isController = feature.includes('contrôleur au choix');
                  return (
                    <li key={idx} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      {isMarking ? (
                        <button
                          onClick={() => setShowMarking(true)}
                          className="text-left text-gray-300 underline decoration-dotted decoration-purple-500/60 underline-offset-4 hover:text-purple-300 transition-colors"
                          data-testid="marking-feature-btn"
                        >
                          {translateFeature(feature, t)}
                          <span className="block text-xs text-purple-400/80 mt-0.5">{specLabels.viewMarking} →</span>
                        </button>
                      ) : isController ? (
                        <button
                          onClick={() => setShowPicker(true)}
                          className="text-left text-gray-300 underline decoration-dotted decoration-purple-500/60 underline-offset-4 hover:text-purple-300 transition-colors"
                          data-testid="controller-feature-btn"
                        >
                          {translateFeature(feature, t)}
                          <span className="block text-xs text-purple-400/80 mt-0.5">{specLabels.viewSheet} →</span>
                        </button>
                      ) : (
                        <span className="text-gray-300">{translateFeature(feature, t)}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <button
                onClick={() => setShowBracelet(true)}
                className="mt-4 inline-flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 border border-purple-500/30 hover:border-purple-500/60 rounded-full px-4 py-2 transition-colors"
                data-testid="bracelet-sheet-btn"
              >
                {specLabels.viewBracelet} →
              </button>
              <DeliveryNotice className="mt-5" />
            </div>

            {/* Stock */}
            <div className="mb-6">
              {isLowStock ? (
                <p className="text-red-400 font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {t.product.lowStock} — {product.stock} {t.product.unitsAvailable}
                </p>
              ) : (
                <p className="text-gray-400">
                  {t.product.inStock} : <span className="text-green-500 font-semibold">{product.stock} {t.product.unitsAvailable}</span>
                </p>
              )}
              {product.minOrder && (
                <p className="text-gray-400 mt-1">
                  {t.product.minOrder} : <span className="text-purple-400 font-semibold">{product.minOrder} {product.minOrder > 1 ? t.product.units : t.product.unit}</span>
                </p>
              )}
            </div>

            {/* Quantity */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">{t.product.quantity} :</label>
              <div className="flex items-center space-x-4">
                <Button onClick={() => setQuantity(Math.max(1, quantity - 1))} variant="outline" className="border-gray-700 text-white hover:bg-gray-800" size="icon"><Minus className="w-4 h-4" /></Button>
                <span className="text-2xl font-semibold w-12 text-center">{quantity}</span>
                <Button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} variant="outline" className="border-gray-700 text-white hover:bg-gray-800" size="icon"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            {/* Total */}
            <div className="mb-6">
              <p className="text-2xl font-bold" data-testid="product-total">{t.product.total} : {pricing.fmt(pricing.htOf(product) * quantity)} HT</p>
              <p className="text-gray-300 text-sm mt-1">{pricing.fmt(pricing.ttcOn(pricing.htOf(product) * quantity))} TTC <span className="text-gray-500 text-xs">· TVA {pricing.vatRate} % incluse</span></p>
            </div>

            {/* Configurateur de marquage */}
            <div className="mb-6 border border-gray-800 rounded-xl p-5 bg-gray-900/40" data-testid="marking-configurator" dir={isRTL ? 'rtl' : 'ltr'}>
              <h3 className="text-white font-semibold mb-4">{markCopy.title}</h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setMarking('none')}
                  data-testid="marking-none"
                  className={`text-start px-4 py-3 rounded-lg border transition-all duration-200 ${marking === 'none' ? 'border-purple-500 bg-purple-600/15 text-white' : 'border-gray-700 bg-transparent text-gray-300 hover:border-gray-500'}`}
                >
                  <span className="block text-sm font-semibold">{markCopy.none}</span>
                  <span className="block text-xs text-gray-400 mt-0.5">{markCopy.noneDesc}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMarking('custom')}
                  data-testid="marking-custom"
                  className={`text-start px-4 py-3 rounded-lg border transition-all duration-200 ${marking === 'custom' ? 'border-purple-500 bg-purple-600/15 text-white' : 'border-gray-700 bg-transparent text-gray-300 hover:border-gray-500'}`}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    {markCopy.custom}
                    <span className="text-[10px] uppercase tracking-wide bg-purple-600 text-white rounded-full px-2 py-0.5">{markCopy.customBadge}</span>
                  </span>
                  <span className="block text-xs text-gray-400 mt-0.5">{markCopy.customDesc}</span>
                </button>
              </div>

              {marking === 'custom' && (
                <div className="mt-5 space-y-4 animate-in fade-in duration-300">
                  {/* Upload logo */}
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">{markCopy.upload}</label>
                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg px-4 py-2.5 border border-gray-700 transition-colors" data-testid="logo-upload-label">
                        <ShoppingCart className="w-4 h-4 hidden" />
                        {uploadingLogo ? markCopy.uploading : (logoUrl ? markCopy.change : markCopy.upload)}
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload-input" disabled={uploadingLogo} />
                      </label>
                      {logoUrl && (
                        <button type="button" onClick={() => setLogoUrl('')} className="text-xs text-red-400 hover:text-red-300" data-testid="logo-remove">{markCopy.remove}</button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">{markCopy.formats}</p>
                  </div>

                  {/* Texte à graver */}
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">{markCopy.textLabel}</label>
                    <input
                      type="text"
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      placeholder={markCopy.textPh}
                      maxLength={40}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-purple-500"
                      data-testid="marking-text-input"
                    />
                  </div>

                  {/* Aperçu indicatif */}
                  <div>
                    <p className="text-xs uppercase tracking-wider text-purple-400 mb-2">{markCopy.previewLabel}</p>
                    <div className="relative rounded-xl overflow-hidden border border-gray-800 bg-black" data-testid="marking-preview">
                      <img src={`${BACKEND_URL}/api/uploads/bracelet-base.webp?v=1`} alt="Aperçu bracelet" className="w-full block" />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="flex flex-col items-center justify-center" style={{ width: '42%', marginLeft: '-6%', marginTop: '2%' }}>
                          {logoUrl && (
                            <img
                              src={logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}${logoUrl}`}
                              alt="Votre logo"
                              className="max-h-12 max-w-full object-contain drop-shadow"
                              data-testid="marking-preview-logo"
                            />
                          )}
                          {customText && (
                            <span className="mt-1 text-gray-900 font-semibold text-[13px] leading-tight text-center" style={{ textShadow: '0 1px 1px rgba(255,255,255,0.4)' }} data-testid="marking-preview-text">{customText}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{markCopy.previewNote}</p>
                  </div>

                  <p className="text-xs text-gray-400 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    {markCopy.delay}
                  </p>
                </div>
              )}
            </div>

            {/* Add to Cart */}
            <Button onClick={addToCart} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg font-semibold transition-colors duration-200" data-testid="product-add-to-cart">
              <ShoppingCart className="w-5 h-5 mr-2" />{t.product.addToCart}
            </Button>

            <div className="mt-6">
              <ShareButtons path={`/produit/${product.slug}`} title={`${product.name} — Party LED`} />
            </div>
          </div>
        </div>

        <ColorSimulator />

        {/* Controller Section */}
        <div className="mt-16" data-testid="controller-section">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Georgia, serif' }}>{t.product.controllerTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {controllers.map(({ key, icon: Icon, img, title, desc }) => (
              <button
                key={key}
                onClick={() => setActiveController({ key, title, desc })}
                className="text-left bg-gray-900 rounded-xl border border-gray-800 overflow-hidden hover:border-purple-600/50 transition-all duration-300 group"
                data-testid={`controller-${key}`}
              >
                <div className="aspect-square overflow-hidden bg-black/40">
                  <img src={img} alt={title} className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5 text-purple-400" />
                    <h3 className="text-lg font-bold text-white">{title}</h3>
                  </div>
                  <p className="text-gray-400 text-sm">{desc}</p>
                  <span className="inline-block mt-3 text-xs uppercase tracking-wider text-purple-400 group-hover:text-purple-300">
                    {specLabels.viewSheet} →
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {activeController && (
          <ControllerSheetModal
            controllerKey={activeController.key}
            title={activeController.title}
            description={activeController.desc}
            onClose={() => setActiveController(null)}
          />
        )}
        {showMarking && <MarkingModal onClose={() => setShowMarking(false)} />}
        {showPicker && (
          <ControllerPickerModal
            controllers={controllers}
            onPick={(c) => {
              setShowPicker(false);
              setActiveController({ key: c.key, title: c.title, desc: c.desc });
            }}
            onClose={() => setShowPicker(false)}
          />
        )}
        {showBracelet && (
          <BraceletSheetModal
            image={product.images?.[0]}
            name={product.name}
            onClose={() => setShowBracelet(false)}
          />
        )}

        {/* Reviews Section */}
        <div className="mt-16" data-testid="reviews-section">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>
              {t.product.reviews} {avgRating && <span className="text-lg font-normal text-gray-400 ml-2">({avgRating} {t.product.avgRating})</span>}
            </h2>
            <Button onClick={() => setShowReviewForm(!showReviewForm)} variant="outline" className="border-purple-600 text-purple-400 hover:bg-purple-600/10" data-testid="write-review-btn">
              <Star className="w-4 h-4 mr-2" />{t.product.writeReview}
            </Button>
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <form onSubmit={submitReview} className="bg-gray-900 rounded-xl p-6 mb-8 border border-gray-800" data-testid="review-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input type="text" required value={reviewForm.name} onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })} placeholder={t.product.reviewName}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-600" data-testid="review-name" />
                <input type="email" required value={reviewForm.email} onChange={(e) => setReviewForm({ ...reviewForm, email: e.target.value })} placeholder={t.product.reviewEmail}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-600" data-testid="review-email" />
              </div>
              <div className="mb-4">
                <label className="text-gray-400 text-sm mb-2 block">{t.product.reviewRating}</label>
                <StarRating rating={reviewForm.rating} size={28} interactive onRate={(r) => setReviewForm({ ...reviewForm, rating: r })} />
              </div>
              <textarea required value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} placeholder={t.product.reviewComment} rows={3}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-600 mb-4 resize-none" data-testid="review-comment" />
              <Button type="submit" disabled={submittingReview} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="review-submit">
                <Send className="w-4 h-4 mr-2" />{submittingReview ? '...' : t.product.reviewSubmit}
              </Button>
            </form>
          )}

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t.product.noReviews}</p>
          ) : (
            <div className="space-y-4" data-testid="reviews-list">
              {reviews.map((review) => (
                <div key={review.id} className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-purple-600/20 flex items-center justify-center text-purple-400 font-bold text-sm">
                        {review.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{review.name}</p>
                        <p className="text-gray-500 text-xs">{new Date(review.createdAt).toLocaleDateString('fr-FR')}</p>
                      </div>
                    </div>
                    <StarRating rating={review.rating} size={14} />
                  </div>
                  <p className="text-gray-300 text-sm mt-2">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
